
import { useState } from 'react';
import { appsUserApiRouterLoginView } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import TosModal from './TosModal';

interface SignInFormProps {
  onSuccess: () => void;
  onCancel?: () => void;
}

const SignInForm = ({ onSuccess, onCancel }: SignInFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showTosModal, setShowTosModal] = useState(false);
  const { toast } = useToast();
  const { checkAuthState } = useAuth();

  const clearErrors = () => {
    setErrors({});
  };

  const performLogin = async (tosAccepted: boolean = false) => {
    setLoading(true);
    clearErrors();

    try {
      const response = await appsUserApiRouterLoginView({
        email,
        password,
        tos_accepted: tosAccepted,
      });
      const { user, message } = response.data;

      if (!user) throw new Error(message);

      // Refresh auth state to update the context with the new session
      await checkAuthState();

      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });

      onSuccess();
    } catch (error: any) {
      // Check for TOS required challenge
      if (error.code === 'tos_required') {
        setShowTosModal(true);
        return;
      }

      // Parse error message to determine field-specific errors
      const errorMessage = error.message || 'An error occurred during sign in';

      if (errorMessage.toLowerCase().includes('email')) {
        setErrors({ email: errorMessage });
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors({ password: errorMessage });
      } else {
        setErrors({ general: errorMessage });
      }

      toast({
        title: "Sign in failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    await performLogin(false);
  };

  const handleTosAccept = async () => {
    setShowTosModal(false);
    await performLogin(true);
  };

  const handleTosCancel = () => {
    setShowTosModal(false);
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSignIn} className="space-y-4">
        {errors.general && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
            {errors.general}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">
            Email
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) clearErrors();
            }}
            placeholder="yourname@example.com"
            className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all ${
              errors.email ? 'border-red-300 focus:ring-red-500' : 'border-adv-darkGray'
            }`}
            required
          />
          {errors.email && (
            <p className="text-sm text-red-600 mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Password
          </label>
          <Input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) clearErrors();
            }}
            placeholder="••••••••"
            className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all ${
              errors.password ? 'border-red-300 focus:ring-red-500' : 'border-adv-darkGray'
            }`}
            required
          />
          {errors.password && (
            <p className="text-sm text-red-600 mt-1">{errors.password}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full btn-primary"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <TosModal
        isOpen={showTosModal}
        onAccept={handleTosAccept}
        onCancel={handleTosCancel}
        loading={loading}
      />
    </>
  );
};

export default SignInForm;
